import React from 'react';

import { ScrollableContainer } from '@ergeon/core-components';
import { Call } from '@twilio/voice-sdk';

import { useInfiniteScroll } from '../../hooks/useInfiniteScroll';
import { MyPhoneCall } from '../../types';
import { NoCallsEmptyState } from '../EmptyStates';
import ListDivider from '../ListDivider';
import LoadingState from '../LoadingState';

import CallListItem from './CallListItem';
import CallHistoryItem from './CallHistoryItem';

type CallsListContentProps = {
  activeCalls: Call[];
  callHistory: MyPhoneCall[];
  hasMore: boolean;
  isLoading: boolean;
  onLoadMore: () => void;
  selectedCallId?: number;
};

const CallsListContent: React.FC<CallsListContentProps> = ({
  activeCalls,
  callHistory,
  hasMore,
  isLoading,
  onLoadMore,
  selectedCallId,
}) => {
  const { observerTarget } = useInfiniteScroll({
    hasMore,
    isLoading,
    onLoadMore,
  });

  const hasActiveCalls = activeCalls.length > 0;
  const hasCallHistory = callHistory.length > 0;

  if (isLoading && !hasActiveCalls && callHistory.length === 0) {
    return <LoadingState />;
  }

  if (!isLoading && !hasActiveCalls && callHistory.length === 0) {
    return <NoCallsEmptyState />;
  }

  const callsListRender = (
    <div className="flex flex-col">
      {/* Active calls section */}
      {hasActiveCalls && (
        <>
          {activeCalls.map((call, index) => {
            const key = call.parameters.CallSid;
            return (
              <React.Fragment key={key}>
                <CallListItem call={call} selectedCallId={selectedCallId} />
                {index < activeCalls.length - 1 && <ListDivider />}
              </React.Fragment>
            );
          })}
        </>
      )}

      {/* Divider between active calls and history */}
      {hasActiveCalls && hasCallHistory && <ListDivider />}

      {/* Call history section */}
      {callHistory.map((call, index) => {
        return (
          <React.Fragment key={`history-${call.id}`}>
            <CallHistoryItem call={call} isSelected={call.id === selectedCallId} />
            {index < callHistory.length - 1 && <ListDivider />}
          </React.Fragment>
        );
      })}
    </div>
  );

  return (
    <div className="flex flex-col flex-1 px-2 overflow-hidden">
      <ScrollableContainer className="px-2" direction="col" maskSize={0}>
        {callsListRender}
        {hasMore && <div className="h-0" ref={observerTarget} />}
        {isLoading && hasMore && <LoadingState />}
      </ScrollableContainer>
    </div>
  );
};

export default CallsListContent;
